import React, { useState, useEffect, useRef, Component } from "react";
import { useNavigate } from "react-router-dom";
import { Loading } from "../components/Loading";
import "./TakeDelivery.css";
import union from "../assets/Union.png";
import { Button, ImageWithFallback } from "../components/Utilities";
import { apiService, API_ENDPOINTS } from "../../services/apiService";
import { useCarContext } from "../CarContext";
import locCard from "../assets/locCard.svg";
import rc_yes from "../assets/rc-yes.svg";
import imgTop from "../assets/imgTop.png";
import imgBottom from "../assets/imgBottom.png";
import rc_no from "../assets/rc-no.svg";
import { LiveTimer } from "../components/CardLayout";
import { RequestDeliveryGatepassModal } from "../components/ModelOverlay/RequestDelivery";
import { UploadPickupProofModal } from "../components/ModelOverlay/UploadPickupProofModal";
import { formatOwnership } from "../DealerHome";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";

// Error Boundary Component
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h3>Something went wrong</h3>
          <p>{this.state.error?.message || "An unexpected error occurred."}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// Utility function to convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!(file instanceof File)) {
      console.error("fileToBase64: Invalid file input:", file);
      reject(new Error("Invalid file input: File object is required"));
      return;
    }
    console.log(
      "fileToBase64: Processing file:",
      file.name,
      file.type,
      file.size
    );
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result;
      if (!result) {
        reject(new Error("Failed to convert file to base64"));
      } else {
        resolve(result);
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

export default function TakeDelivery() {
  const navigate = useNavigate();
  const { setTakeDeliveryCount, setNocCount } = useCarContext();
  const [
    isRequestDeliveryGatepassModalOpen,
    setIsRequestDeliveryGatepassModalOpen,
  ] = useState(false);
  const [isUploadPickupProofModalOpen, setIsUploadPickupProofModalOpen] =
    useState(false);
  const [isViewPickupProofModalOpen, setIsViewPickupProofModalOpen] =
    useState(false);
  const [selectedCarId, setSelectedCarId] = useState(null);
  const [clickedCarForGatepass, setClickedCarForGatepass] = useState(null); // NEW
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapValue, setMapValue] = useState(null);
  const [otpAccessForm, setOtpAccessForm] = useState({
    phoneNumber: "",
    userName: "",
    userRole: "delivery_driver",
    orgId: "trustauto",
    validityDays: 1,
    validityMinutes: 0,
    purpose: "Testing OTP Login",
    description: "Test access for OTP login functionality",
    landingPage: "/driverHome",
  });

  useEffect(() => {
    const fetchDeliveryCars = async () => {
      try {
        setLoading(true);
        const response = await apiService.getDealerDeliveryCars();
        console.log(
          "getDealerDeliveryCars response:",
          JSON.stringify(response.data, null, 2)
        );

        if (!response || !response.data || !response.data.yardGroups) {
          console.warn("No yardGroups found in response:", response);
          setError("No cars available for delivery.");
          setCars([]);
          setTakeDeliveryCount(0);
          setLoading(false);
          return;
        }

        setMapValue(response.data.yardGroups);
        const validCars = response.data.yardGroups
          .flatMap((group) => group.cars || [])
          .map((car, index) => {
            const originalCarId =
              car.id || `car-${Math.random().toString(36).substr(2, 9)}`;
            return {
              id: `${originalCarId}-Delivery-${index}`,
              originalCarId,
              brand: car.make || "Unknown",
              model: car.model || "Unknown",
              rto: car.regno || "N/A",
              fuel: car.fueltype || "Unknown",
              transmission: car.transmission || "N/A",
              year: Number(car.year) || 0,
              km: Number(car.kmsrun) || 0,
              location: car.yard,
              price:
                Number(car.latestBidValue) ||
                (Number(car.auctionWinAmount) || 0) +
                  (Number(car.auctionDepositAmount) || 0),
              startDate: car["headFinanceApprovedAt"],
              endDate: car["next_deadline"],
              evaluationImages: car.evaluationImages || [],
              car_image_urls: car.car_image_urls || [],
              rc:
                car.rcstatus?.toUpperCase() === "YES" ||
                car.rcstatus?.toUpperCase() === "Y" ||
                car.rcstatus?.toUpperCase() === "AVAILABLE",
              owner: formatOwnership(car.ownership),
              auctionId: car.auctionId || "Unknown",
              flowEntityId: car.flow_entity_id || "Unknown",
              state: car.state,
              delivery_gatepass_id: car.delivery_gatepass_id || null,
              delivery_link_id: car.delivery_link_id || null,
              delivery_photos: car.delivery_photos || [],
              driverId: car.driverId || null,
            };
          });

        console.log("Processed validCars:", JSON.stringify(validCars, null, 2));
        if (validCars.length === 0) {
          setError("No cars available for delivery.");
          setTakeDeliveryCount(0);
        } else {
          setTakeDeliveryCount(validCars.length);
        }
        setCars(validCars);
        setError(null);
      } catch (err) {
        console.error("Error fetching dealer delivery cars:", {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data,
        });
        let errorMessage = "Failed to load cars. Please try again.";
        if (err.response?.status === 401) {
          errorMessage = "Unauthorized. Redirecting to login.";
          navigate("/login");
        } else if (err.response?.status === 400) {
          errorMessage = `Invalid request: ${
            err.response.data?.message || "Please check your inputs."
          }`;
        } else if (err.response?.status === 500) {
          errorMessage = `Server error: ${
            err.response.data?.message || "Please try again later."
          }`;
        } else if (err.code === "ECONNABORTED") {
          errorMessage = "Request timed out. Please check your network.";
        }
        setError(errorMessage);
        setCars([]);
        setTakeDeliveryCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveryCars();
  }, [navigate, setTakeDeliveryCount]);

  const handleRequestGatepass = (selectedCarIds) => {
    setCars((prev) => {
      const newCars = prev.map((car) =>
        selectedCarIds.includes(car.id)
          ? { ...car, state: "DELIVERY_GATE_PASS_REQUESTED" }
          : car
      );
      setTakeDeliveryCount(newCars.length);
      return newCars;
    });
  };

  const handlePhotosUploaded = (carId, photos) => {
    setCars((prev) => {
      const newCars = prev.map((car) =>
        car.id === carId ? { ...car, delivery_photos: photos } : car
      );
      setTakeDeliveryCount(newCars.length);
      return newCars;
    });
  };

  const handleConfirmDelivery = async (carId) => {
    try {
      setLoading(true);
      setError(null);

      const selectedCar = cars.find((car) => car.id === carId);
      if (!selectedCar || !selectedCar.flowEntityId) {
        throw new Error("Selected car or flow entity ID is missing.");
      }

      const confirmData = {
        flowEntityId: selectedCar.flowEntityId,
      };

      const response = await apiService.confirmDelivery(confirmData);

      setCars((prev) => {
        const newCars = prev.filter((car) => car.id !== carId);
        setTakeDeliveryCount((prevCount) => prevCount - 1);
        setNocCount((prevCount) => prevCount + 1);
        return newCars;
      });
    } catch (err) {
      console.error("Error confirming delivery:", err);
      setError("Failed to confirm delivery. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDeliveryLink = async (carId) => {
    const selectedCar = cars.find((car) => car.id === carId);
    if (selectedCar.delivery_link_id) {
      setSelectedCarId(carId);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (
        !selectedCar ||
        !selectedCar.originalCarId ||
        !selectedCar.auctionId ||
        !selectedCar.driverId
      ) {
        throw new Error("Missing required car data.");
      }

      const driverResponse = await apiService.getDrivers("delivery_driver");
      const matchedDriver = driverResponse.data.find(
        (driver) => driver.id === selectedCar.driverId
      );

      const baseurl = window.location.origin;
      if (matchedDriver) {
        const carOtpAccessForm = {
          ...otpAccessForm,
          userName: matchedDriver.name,
          phoneNumber: matchedDriver.phone,
          baseurl: baseurl,
        };
        await apiService.createOTPLoginAccess(carOtpAccessForm);
      }

      setOtpAccessForm((prev) => ({
        ...prev,
        userName: "",
        phoneNumber: "",
      }));

      const requestData = {
        carId: selectedCar.originalCarId,
        auctionId: selectedCar.auctionId,
      };

      const response = await apiService.generateDeliveryLink(requestData);
      const deliveryLinkId = response.data?.deliveryLinkId;

      if (!deliveryLinkId) throw new Error("No delivery link ID returned.");

      setCars((prev) =>
        prev.map((car) =>
          car.id === carId ? { ...car, delivery_link_id: deliveryLinkId } : car
        )
      );

      setSelectedCarId(carId);
    } catch (err) {
      setError("Failed to generate delivery link.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUploadPickupProof = (carId) => {
    setSelectedCarId(carId);
    setIsUploadPickupProofModalOpen(true);
  };

  const handleCloseUploadPickupProofModal = () => {
    setIsUploadPickupProofModalOpen(false);
    setSelectedCarId(null);
  };

  const handleOpenViewPickupProofModal = (carId) => {
    setSelectedCarId(carId);
    setIsViewPickupProofModalOpen(true);
  };

  const handleCloseViewPickupProofModal = () => {
    setIsViewPickupProofModalOpen(false);
    setSelectedCarId(null);
  };

  // UPDATED: Pass car ID to modal
  const handleOpenRequestDeliveryGatepassModal = (e, carId) => {
    e.stopPropagation();
    setClickedCarForGatepass(carId);
    setIsRequestDeliveryGatepassModalOpen(true);
  };

  const handleCloseRequestDeliveryGatepassModal = () => {
    setIsRequestDeliveryGatepassModalOpen(false);
    setClickedCarForGatepass(null);
  };

  const handleSeeMoreCars = () => {
    navigate("/DealerHome");
  };

  return (
    <ErrorBoundary>
      <div className="take-delivery">
        <div className="content-wrapper">
          <div className="main-content">
            <div className="car-grid-container">
              {loading && <Loading />}
              {error && (
                <div className="no-results">
                  <h3>Error</h3>
                  <p>{error}</p>
                  <Button
                    className="see-more"
                    variant="primary"
                    onClick={handleSeeMoreCars}
                  >
                    Back to Home
                  </Button>
                </div>
              )}
              {!loading && !error && cars.length === 0 && (
                <div className="no-results">
                  <h3>No cars found</h3>
                  <p>No car scheduled for delivery.</p>
                  <Button
                    className="see-more"
                    variant="primary"
                    onClick={handleSeeMoreCars}
                  >
                    Back to Home
                  </Button>
                </div>
              )}
              {!loading && !error && cars.length > 0 && (
                <div className="car-grid">
                  {cars.map((car) => (
                    <div
                      key={car.id}
                      className="car-card"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/car-details/${car.originalCarId}`);
                      }}
                    >
                      <div className="car-image-wrapper">
                        <img src={imgTop} className="imgTop" alt="" />
                        <img src={imgBottom} className="imgBottom" alt="" />
                        {car.evaluationImages &&
                        car.evaluationImages.length > 0 ? (
                          <Swiper
                            modules={[Pagination, Autoplay]}
                            spaceBetween={0}
                            slidesPerView={1}
                            loop={true}
                            autoplay={{
                              delay: 2500,
                              disableOnInteraction: false,
                            }}
                            pagination={{ clickable: true }}
                            className="car-image-carousel"
                            allowTouchMove={true}
                          >
                            {car.evaluationImages.map((img, idx) => (
                              <SwiperSlide key={img.id || idx}>
                                <div className="carousel-image-container">
                                  <ImageWithFallback
                                    src={img.url}
                                    alt={`${car.brand} ${car.model} - ${
                                      img.angle || idx + 1
                                    }`}
                                    className="car-image"
                                  />
                                </div>
                              </SwiperSlide>
                            ))}
                          </Swiper>
                        ) : (
                          <ImageWithFallback
                            src={
                              car.car_image_urls &&
                              car.car_image_urls.length > 0
                                ? car.car_image_urls[0].url ||
                                  car.car_image_urls[0]
                                : "https://via.placeholder.com/150"
                            }
                            alt="Steering Wheel"
                            className="car-image"
                          />
                        )}
                        <LiveTimer
                          startDateStr={car.startDate}
                          endDateStr={car.endDate}
                        />
                        <div className="car-location">
                          <ImageWithFallback src={locCard} alt="Location" />
                          <p className="car-location-name">{car.location}</p>
                        </div>
                      </div>
                      <div className="car-details">
                        <div className="car-info">
                          <h3 className="car-title">
                            {car.year} {car.brand} {car.model}
                          </h3>
                          <div className="car-spec-container">
                            <div className="car-specs">
                              <span className="distance">
                                {car.rc ? (
                                  <img src={rc_yes} alt="" className="rc" />
                                ) : (
                                  <img src={rc_no} alt="" className="rc" />
                                )}
                                {car.km.toLocaleString()} kms
                              </span>
                              <span className="car-fuel">
                                {" • "}
                                {car.fuel} • {car.transmission}
                              </span>
                            </div>
                            <div className="car-tags">
                              {car.rc ? (
                                <img src={rc_yes} alt="RC Yes" className="rc" />
                              ) : (
                                <img src={rc_no} alt="RC No" className="rc" />
                              )}
                              <span className="rto-tag">{car.rto}</span>
                              <span className="owner-tag">{car.owner}</span>
                            </div>
                            <div className="phone-car-location">
                              <ImageWithFallback src={locCard} alt="Location" />
                              <p className="car-location-name">
                                {car.location}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="car-actions">
                          {car.state === "PAYMENT_APPROVED" && (
                            <Button
                              className="btn-full"
                              variant="primary"
                              onClick={(e) =>
                                handleOpenRequestDeliveryGatepassModal(e, car.id)
                              }
                            >
                              Request Delivery Gatepass
                            </Button>
                          )}

                          {car.state === "DELIVERY_GATE_PASS_REJECTED" && (
                            <Button
                              className="btn-full"
                              variant="primary"
                              onClick={(e) =>
                                handleOpenRequestDeliveryGatepassModal(e, car.id)
                              }
                            >
                              Request Delivery Gatepass
                            </Button>
                          )}

                          {car.state === "DELIVERY_GATE_PASS_REQUESTED" && (
                            <Button
                              className="btn-full"
                              variant="primary"
                              disabled
                            >
                              Pending Gatepass
                            </Button>
                          )}
                          {car.state === "DELIVERY_GATE_PASS_APPROVED" && (
                            <>
                              {car.delivery_photos &&
                              car.delivery_photos.length > 0 ? (
                                <>
                                  <Button
                                    className="btn-full"
                                    variant="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenViewPickupProofModal(car.id);
                                    }}
                                  >
                                    View
                                  </Button>
                                  <Button
                                    className="btn-full"
                                    variant="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleConfirmDelivery(car.id);
                                    }}
                                  >
                                    Confirm
                                  </Button>
                                </>
                              ) : car.delivery_link_id ? (
                                <Button
                                  className="btn-full"
                                  variant="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenUploadPickupProof(car.id);
                                  }}
                                >
                                  Upload Pickup Proof
                                </Button>
                              ) : (
                                <Button
                                  className="btn-full"
                                  variant="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleGenerateDeliveryLink(car.id);
                                  }}
                                >
                                  Share Delivery Link
                                </Button>
                              )}
                            </>
                          )}
                          {car.state === "DELIVERED" && (
                            <Button
                              className="btn-full"
                              variant="primary"
                              disabled
                            >
                              Delivered
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="mob-btn">
                        {car.state === "PAYMENT_APPROVED" && (
                          <Button
                            className="btn-full"
                            variant="primary"
                            onClick={(e) =>
                              handleOpenRequestDeliveryGatepassModal(e, car.id)
                            }
                          >
                            Request Delivery Gatepass
                          </Button>
                        )}
                        {car.state === "DELIVERY_GATE_PASS_REJECTED" && (
                          <Button
                            className="btn-full"
                            variant="primary"
                            onClick={(e) =>
                              handleOpenRequestDeliveryGatepassModal(e, car.id)
                            }
                          >
                            Request Delivery Gatepass
                          </Button>
                        )}
                        {car.state === "DELIVERY_GATE_PASS_REQUESTED" && (
                          <Button
                            className="btn-full"
                            variant="primary"
                            disabled
                          >
                            Pending Gatepass
                          </Button>
                        )}
                        {car.state === "DELIVERY_GATE_PASS_APPROVED" && (
                          <>
                            {car.delivery_photos &&
                            car.delivery_photos.length > 0 ? (
                              <>
                                <Button
                                  className="btn-full"
                                  variant="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenViewPickupProofModal(car.id);
                                  }}
                                >
                                  View
                                </Button>
                                <Button
                                  className="btn-full"
                                  variant="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleConfirmDelivery(car.id);
                                  }}
                                >
                                  Confirm Delivery
                                </Button>
                              </>
                            ) : car.delivery_link_id ? (
                              <Button
                                className="btn-full"
                                variant="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenUploadPickupProof(car.id);
                                }}
                              >
                                Upload Pickup Proof
                              </Button>
                            ) : (
                              <Button
                                className="btn-full"
                                variant="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGenerateDeliveryLink(car.id);
                                }}
                              >
                                Share Delivery Link
                              </Button>
                            )}
                          </>
                        )}
                        {car.state === "DELIVERED" && (
                          <Button
                            className="btn-full"
                            variant="primary"
                            disabled
                          >
                            Delivered
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* UPDATED: Pass clickedCarForGatepass */}
        <RequestDeliveryGatepassModal
          isOpen={isRequestDeliveryGatepassModalOpen}
          onClose={handleCloseRequestDeliveryGatepassModal}
          cars={cars}
          onRequestGatepass={handleRequestGatepass}
          yardGroups={mapValue}
          clickedCarId={clickedCarForGatepass}
        />
        <UploadPickupProofModal
          isOpen={isUploadPickupProofModalOpen}
          onClose={handleCloseUploadPickupProofModal}
          cars={cars}
          selectedCars={selectedCarId ? [selectedCarId] : []}
          onPhotosUploaded={handlePhotosUploaded}
          isViewMode={false}
        />
        <UploadPickupProofModal
          isOpen={isViewPickupProofModalOpen}
          onClose={handleCloseViewPickupProofModal}
          cars={cars}
          selectedCars={selectedCarId ? [selectedCarId] : []}
          onPhotosUploaded={handlePhotosUploaded}
          isViewMode={true}
        />
      </div>
    </ErrorBoundary>
  );
}